<template>
  <div class="custom-content-editor" :class="[customContentList.length && 'has-custom-content', disabled && 'disabled']">
    <draggable
      v-model="customContentList"
      tag="div"
      class="custom-content-list"
      handle=".content-head .drag-btn"
      animation="200"
      ghostClass="ghost"
      :disabled="disabled"
      @update="onDragUpdate"
    >
      <div
        v-for="(content, contentIndex) in customContentList"
        class="custom-content"
        :key="content.id"
        :class="[content.error && 'is-error', disabled && 'disabled']"
      >
        <template v-if="allowEdit">
          <div class="content-head">
            <div v-if="showContentHeadTitle && content.type !== CUSTOM_CONTENT_TYPE.JOB_DESCRIPTION" class="title">{{ $t(`${i18nPrefix}.title.customContent.${content.type}`) }}</div>
            <div v-if="showHiddenPaddingCheckbox" class="hidden-padding-checkbox">
              <span class="label" :class="content.hiddenPadding && 'is-checked'" @click="handleClickHiddenPadding(content)">{{ $t(`${i18nPrefix}.label.hiddenPadding`) }}</span>
              <el-checkbox v-model="content.hiddenPadding"></el-checkbox>
            </div>
            <div v-if="content.error && content.type !== CUSTOM_CONTENT_TYPE.JOB_DESCRIPTION" class="content-error">{{ content.error }}</div>
            <div v-if="content.type !== CUSTOM_CONTENT_TYPE.JOB_DESCRIPTION" class="edit-btn delete-btn" @click="handleDeleteBtnClick(content, contentIndex)">
              <i class="iconfont iconDelete"></i>
            </div>
            <el-divider v-if="content.type !== CUSTOM_CONTENT_TYPE.JOB_DESCRIPTION" class="btn-divider" direction="vertical"></el-divider>
            <div class="edit-btn drag-btn">
              <i class="iconfont iconMore"></i>
            </div>
          </div>
          <div v-if="showContentTitle && content.type !== CUSTOM_CONTENT_TYPE.JOB_DESCRIPTION" class="content-title">
            <el-input v-model="content.title" :placeholder="$t(`${i18nPrefix}.placeholder.customContent.title`)"/>
          </div>
        </template>
        <div v-if="content.type !== CUSTOM_CONTENT_TYPE.JOB_DESCRIPTION" class="content-item-list">
          <template v-for="(item, itemIndex) in content.itemList">
            <div
              v-if="content.type === CUSTOM_CONTENT_TYPE.VIDEO"
              class="content-item video-item"
              :key="item.id"
            >
              <Uploader
                v-model="item.coverFile"
                class="video-cover-uploader"
                :ref="`videoCoverUploader_${content.id}_${item.id}`"
                :file-type="FILE_TYPE.IMAGE"
                :label="$t(`${i18nPrefix}.btn.customContent.addVideoCover`)"
                :placeholder="$t(`${i18nPrefix}.placeholder.customContent.videoCoverSize`)"
                :disabled="disabled"
                :file-key-generator="(file) => generateFileKey(content.type, `video_cover_${item.id}`, file)"
              />
              <Uploader
                v-model="item.file"
                class="video-uploader"
                :ref="`videoUploader_${content.id}_${item.id}`"
                :file-type="FILE_TYPE.VIDEO"
                :accept-file-size="400"
                :label="$t(`${i18nPrefix}.btn.customContent.addVideo`)"
                :placeholder="$t(`${i18nPrefix}.placeholder.customContent.videoSize`)"
                :disabled="disabled"
                :file-key-generator="(file) => generateFileKey(content.type, `video_${item.id}`, file)"
              />
              <el-input
                v-model="item.description"
                class="video-desc"
                :placeholder="$t(`${i18nPrefix}.placeholder.customContent.videoDescription`)"
              />
            </div>
            <div
              v-else-if="content.type === CUSTOM_CONTENT_TYPE.CAROUSEL"
              class="content-item carousel-item"
              :key="item.id"
            >
              <Uploader
                v-model="item.file"
                class="carousel-image-uploader"
                :ref="`carouselUploader_${content.id}_${item.id}`"
                :file-type="isVideoItem(item) ? FILE_TYPE.VIDEO : FILE_TYPE.IMAGE"
                :accept-file-type="carouselAcceptFileType"
                :accept-file-size-map="carouselFileSizeMap"
                :label="$t(`${i18nPrefix}.btn.customContent.${carouselSupportVideo ? 'addImageOrVideo' : 'addImage'}`)"
                :placeholder="$t(`${i18nPrefix}.placeholder.customContent.${carouselSupportVideo ? 'imageOrVideoSize' : 'imageSize'}`, { fileSize: carouselVideoMaxSize })"
                :disabled="disabled"
                :file-key-generator="(file) => generateFileKey(content.type, `carousel_${item.id}`, file)"
                @file-selected="handleCarouselImageSelected(content, item, itemIndex, $event)"
                @file-replaced="handleCarouselImageReplaced(content, item, itemIndex, $event)"
                @file-deleted="handleCarouselImageDeleted(content, item, itemIndex)"
              />
              <el-input
                v-model="item.description"
                class="carousel-image-desc"
                :placeholder="$t(`${i18nPrefix}.placeholder.customContent.imageDescription`)"
              />
              <el-input
                v-model="item.link"
                class="carousel-image-url"
                :placeholder="$t(`${i18nPrefix}.placeholder.customContent.imageLink`)"
              >
                <template #prepend>
                  <i class="el-icon-link"></i>
                </template>
              </el-input>
            </div>
            <div
              v-else-if="content.type === CUSTOM_CONTENT_TYPE.PICTURE"
              class="content-item picture-item"
              :key="item.id"
            >
              <Uploader
                v-model="item.file"
                class="picture-image-uploader"
                :ref="`pictureUploader_${content.id}_${item.id}`"
                :file-type="FILE_TYPE.IMAGE"
                :label="$t(`${i18nPrefix}.btn.customContent.addPicture`)"
                :placeholder="$t(`${i18nPrefix}.placeholder.customContent.pictureSize`)"
                :disabled="disabled"
                :file-key-generator="(file) => generateFileKey(content.type, `picture_${item.id}`, file)"
                @file-selected="handlePictureImageSelected(content, item, itemIndex)"
                @file-deleted="handleCarouselImageDeleted(content, item, itemIndex)"
              />
              <el-input
                v-model="item.description"
                class="carousel-image-desc"
                :placeholder="$t(`${i18nPrefix}.placeholder.customContent.imageDescription`)"
              />
              <el-input
                v-model="item.link"
                class="carousel-image-url"
                :placeholder="$t(`${i18nPrefix}.placeholder.customContent.imageLink`)"
              >
                <template #prepend>
                  <i class="el-icon-link"></i>
                </template>
              </el-input>
            </div>
            <div
              v-else-if="content.type === CUSTOM_CONTENT_TYPE.TEXT"
              class="content-item text-item"
              :key="item.id"
            >
              <TinymceEditor
                v-model="item.text"
                hiddenImage
                :disabled="disabled"
                :ref="`textEditor_${content.id}`"
              ></TinymceEditor>
            </div>
          </template>
        </div>
        <div v-else class="content-item-list">
          <div class="content-item job-description-item">{{ $t(jobDescriptionTipKey) }}</div>
        </div>
      </div>
    </draggable>
    <div v-if="allowEdit" class="add-btn-container">
      <div v-if="showAddText" class="add-btn add-text-btn" @click="handleAddTextBtnClick">
        <div class="btn-icon"></div>
        <el-button class="btn-label" type="text">{{ $t(`${i18nPrefix}.btn.customContent.addText`) }}</el-button>
      </div>
      <div class="add-btn add-carousel-btn" @click="handleAddCarouselBtnClick">
        <div class="btn-icon"></div>
        <el-button class="btn-label" type="text">{{ $t(`${i18nPrefix}.btn.customContent.addCarouselContent`) }}</el-button>
      </div>
      <div v-if="showAddPictureBoard" class="add-btn add-picture-btn" @click="handleAddPictureBoardBtnClick">
        <div class="btn-icon"></div>
        <el-button class="btn-label" type="text">{{ $t(`${i18nPrefix}.btn.customContent.addPictureBoard`) }}</el-button>
      </div>
      <div class="add-btn add-video-btn" @click="handleAddVideoBtnClick">
        <div class="btn-icon"></div>
        <el-button class="btn-label" type="text">{{ $t(`${i18nPrefix}.btn.customContent.addVideContent`) }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import draggable from 'vuedraggable'
import Uploader, { FILE_TYPE } from './uploader.vue'
import { mapGetters } from 'vuex'

const CUSTOM_CONTENT_TYPE = {
  VIDEO: 'video',
  CAROUSEL: 'silder',
  TEXT: 'text',
  PICTURE: 'picture',
  JOB_DESCRIPTION: 'job-detail-description'
}

export default {
  components: {
    draggable,
    Uploader,
    TinymceEditor: () => import('./tinymceEditor.vue')
  },
  props: {
    contentList: {
      type: Array,
      required: false
    },
    showAddText: {
      type: Boolean,
      default: false
    },
    showAddPictureBoard: {
      type: Boolean,
      default: false
    },
    showContentHeadTitle: {
      type: Boolean,
      default: false
    },
    // 是否允许增加项，若为false，则底部添加区域和每项的头部区域（拖动， 删除， 标题输入）不显示
    allowEdit: {
      type: Boolean,
      default: true
    },
    // 是否显示标题输入框
    showContentTitle: {
      type: Boolean,
      default: true
    },
    jobDescriptionTipKey: {
      type: String,
      default: ''
    },
    showHiddenPaddingCheckbox: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    carouselSupportVideo: {
      type: Boolean,
      default: false
    },
    carouselVideoMaxSize: {
      type: Number,
      default: 10
    }
  },
  data () {
    return {
      CUSTOM_CONTENT_TYPE,
      FILE_TYPE,
      i18nPrefix: 'admin.job.editor',
      customContentList: [],
      vid: 0
    }
  },
  computed: {
    pageNum () {
      return this.$route.query.page_num
    },
    carouselAcceptFileType () {
      const fileTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/jpg']
      return this.carouselSupportVideo ? fileTypes.concat('video/mp4') : fileTypes
    },
    carouselFileSizeMap () {
      return this.carouselSupportVideo ? { 'video/mp4': this.carouselVideoMaxSize } : {}
    }
  },
  created () {

  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    async submit () {
      let hasError = false
      const contentList = []
      const uploaderDataMap = {}
      let hasJobDescription = false
      _.forEach(this.customContentList, content => {
        const contentInfoItems = []
        const contentInfo = {
          type: content.type,
          title: content.title,
          hiddenPadding: content.hiddenPadding,
          items: contentInfoItems
        }
        if (!hasJobDescription) {
          hasJobDescription = content.type === CUSTOM_CONTENT_TYPE.JOB_DESCRIPTION
        }
        content.error = null
        _.forEach(content.itemList, item => {
          if (!item.file && !item.description && !item.link && !item.coverFile && !item.text) {
            return
          }
          const itemInfo = {
            resource_id: item.resource_id,
            resource_key: item.file,
            text: item.text,
            description: item.description,
            target_url: item.link,
            cover_resource_id: item.cover_resource_id,
            cover_resource_key: item.coverFile
          }
          if (this.carouselSupportVideo && content.type === CUSTOM_CONTENT_TYPE.CAROUSEL) {
            itemInfo.resource_type = this.isVideoItem(item) ? 'video' : 'image'
          }
          contentInfoItems.push(itemInfo)
          if (content.type === CUSTOM_CONTENT_TYPE.VIDEO) {
            uploaderDataMap[`videoUploader_${content.id}_${item.id}`] = itemInfo
            uploaderDataMap[`videoCoverUploader_${content.id}_${item.id}`] = itemInfo
          } else if (content.type === CUSTOM_CONTENT_TYPE.CAROUSEL) {
            uploaderDataMap[`carouselUploader_${content.id}_${item.id}`] = itemInfo
          } else if (content.type === CUSTOM_CONTENT_TYPE.PICTURE) {
            uploaderDataMap[`pictureUploader_${content.id}_${item.id}`] = itemInfo
          }
          if (!item.file && content.type !== CUSTOM_CONTENT_TYPE.TEXT) {
            if (content.type === CUSTOM_CONTENT_TYPE.VIDEO) {
              content.error = this.$t(`${this.i18nPrefix}.message.requireCustomVideoFile`)
            } else {
              content.error = this.$t(`${this.i18nPrefix}.message.requireCustomCarouselFile`)
            }
            hasError = true
          }
          if (content.type === CUSTOM_CONTENT_TYPE.VIDEO && !item.coverFile) {
            content.error = this.$t(`${this.i18nPrefix}.message.requireCustomVideoCoverFile`)
            hasError = true
          }
        })
        if (content.type !== CUSTOM_CONTENT_TYPE.JOB_DESCRIPTION && contentInfoItems.length < 1) {
          hasError = true
          if (content.type === CUSTOM_CONTENT_TYPE.TEXT) {
            content.error = this.$t(`${this.i18nPrefix}.message.requireCustomText`)
          } else if (content.type === CUSTOM_CONTENT_TYPE.VIDEO) {
            content.error = this.$t(`${this.i18nPrefix}.message.requireCustomVideo`)
          } else if (content.type === CUSTOM_CONTENT_TYPE.PICTURE) {
            content.error = this.$t(`${this.i18nPrefix}.message.requireCustomPicture`)
          } else {
            content.error = this.$t(`${this.i18nPrefix}.message.requireCustomCarousel`)
          }
        }
        contentList.push(contentInfo)
      })
      if (_.includes(['4', '14'], this.pageNum) && (!contentList.length || !hasJobDescription)) {
        contentList.push({
          items: [],
          order: contentList.length ? contentList.length + 1 : 1,
          title: '',
          type: 'job-detail-description'
        })
      }
      if (hasError) {
        this.$nextTick(() => {
          this.$el.querySelector('.custom-content.is-error').scrollIntoView({ behavior: 'smooth', block: 'center' })
        })
        return null
      } else {
        await Promise.all(_.map(uploaderDataMap, (dataItem, refId) => {
          const uploaderComponent = this.$refs[refId][0]
          const uploadMode = dataItem.resource_type === 'video' ? 'job_description_video' : `job_description_${refId.startsWith('videoUploader_') ? 'video' : 'image'}`
          return uploaderComponent.uploadSelectedFile(uploadMode).then(fileKey => {
            if (refId.startsWith('videoCoverUploader_')) {
              dataItem.cover_resource_key = fileKey
            } else {
              dataItem.resource_key = fileKey
            }
          })
        }))
        return contentList
      }
    },
    isVideoItem (item) {
      return item?.resource_type === 'video' || item?.file?.type === 'video/mp4'
    },
    generateFileKey (contentType, contentItemId, file) {
      const account = this.getCurrentAccount()
      const fileKey = `job/custom_content/${contentType}/${contentItemId}/${account.id}/${new Date().valueOf()}`
      return fileKey
    },
    createContentItemData () {
      return {
        id: ++this.vid,
        description: null,
        link: null,
        file: null,
        coverFile: null
      }
    },
    handleDeleteBtnClick (content, contentIndex) {
      if (this.disabled) {
        return
      }
      this.customContentList.splice(contentIndex, 1)
    },
    handleCarouselImageSelected (content, imageItem, itemIndex, file) {
      if (imageItem) {
        this.$set(imageItem, 'resource_type', file?.type === 'video/mp4' ? 'video' : 'image')
      }
      if (content.itemList.length >= 10) {
        return
      }
      content.itemList.push(this.createContentItemData())
    },
    handleCarouselImageReplaced (content, imageItem, itemIndex, { newFile }) {
      this.$set(imageItem, 'resource_type', newFile?.type === 'video/mp4' ? 'video' : 'image')
    },
    handlePictureImageSelected (content, imageItem, itemIndex) {
      if (content.itemList.length >= 3) {
        return
      }
      content.itemList.push(this.createContentItemData())
    },
    handleCarouselImageDeleted (content, imageItem, itemIndex) {
      this.$set(imageItem, 'resource_type', null)
      if (
        (content.type === CUSTOM_CONTENT_TYPE.CAROUSEL && content.itemList.length >= 7) ||
        (content.type === CUSTOM_CONTENT_TYPE.PICTURE && content.itemList.length >= 3)
      ) {
        content.itemList.push(this.createContentItemData())
      }
      content.itemList.splice(itemIndex, 1)
    },
    handleAddTextBtnClick () {
      if (this.disabled) {
        return
      }
      const customContentList = this.customContentList
      customContentList.push({
        id: ++this.vid,
        type: CUSTOM_CONTENT_TYPE.TEXT,
        hiddenPadding: false,
        title: null,
        error: null,
        itemList: [this.createContentItemData()]
      })
    },
    handleAddPictureBoardBtnClick () {
      if (this.disabled) {
        return
      }
      const customContentList = this.customContentList
      customContentList.push({
        id: ++this.vid,
        type: CUSTOM_CONTENT_TYPE.PICTURE,
        title: null,
        hiddenPadding: false,
        error: null,
        itemList: [this.createContentItemData()]
      })
    },
    handleAddCarouselBtnClick () {
      if (this.disabled) {
        return
      }
      const customContentList = this.customContentList
      customContentList.push({
        id: ++this.vid,
        type: CUSTOM_CONTENT_TYPE.CAROUSEL,
        title: null,
        hiddenPadding: false,
        error: null,
        itemList: [this.createContentItemData()]
      })
    },
    handleAddVideoBtnClick () {
      if (this.disabled) {
        return
      }
      const customContentList = this.customContentList
      customContentList.push({
        id: ++this.vid,
        type: CUSTOM_CONTENT_TYPE.VIDEO,
        title: null,
        hiddenPadding: false,
        error: null,
        itemList: [this.createContentItemData()]
      })
    },
    onDragUpdate ({ oldIndex, newIndex }) {
      this.resetTextEditor(oldIndex)
      this.resetTextEditor(newIndex)
    },
    resetTextEditor (index) {
      const customContent = this.customContentList[index]
      if (customContent.type === CUSTOM_CONTENT_TYPE.TEXT) {
        const textEditor = this.$refs[`textEditor_${customContent.id}`][0]
        textEditor.resetEditor()
      }
    },
    handleClickHiddenPadding (content) {
      this.$set(content, 'hiddenPadding', !content.hiddenPadding)
    }
  },
  watch: {
    contentList: {
      immediate: true,
      handler () {
        this.customContentList = _.map(this.contentList, content => {
          const contentData = {
            ...content,
            id: ++this.vid,
            error: null,
            itemList: _.map(content.items, item => {
              return {
                ...item,
                id: ++this.vid,
                description: item.description,
                link: item.target_url,
                file: item.resource_key,
                coverFile: item.cover_resource_key
              }
            })
          }
          if (contentData.type === CUSTOM_CONTENT_TYPE.CAROUSEL) {
            this.handleCarouselImageSelected(contentData)
          }
          if (contentData.type === CUSTOM_CONTENT_TYPE.PICTURE) {
            this.handlePictureImageSelected(contentData)
          }
          return contentData
        })
        if (!this.customContentList.length && _.includes(['4', '14'], this.pageNum)) {
          this.customContentList.push({
            items: [],
            order: 1,
            title: '',
            type: 'job-detail-description'
          })
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.custom-content-editor {
  margin-top: 20px;

  &.disabled {
    .add-btn-container {
      .add-btn {
        cursor: not-allowed;
      }
    }
  }

  .custom-content-list {
    .custom-content {
      padding-top: 15px;
      margin-bottom: 30px;

      &.is-error {
        &.no-title {
          .content-title {
            ::v-deep {
              .el-input__inner {
                border-color: #F56C6C;
              }
            }
          }
        }
      }

      &.disabled {
        color: #c0c0c0;
        cursor: not-allowed;

        .content-head {
          .edit-btn {
            &.delete-btn {
               cursor: not-allowed;
            }

            &.drag-btn {
              cursor: not-allowed;
            }
          }
        }

        .carousel-image-url {
          ::v-deep {
            .el-input-group__prepend {
              border-color: #e4e7ed;
            }
          }
        }
      }

      + .custom-content {
        border-top: 1px solid #DCDFE6;
      }

      .content-head {
        display: flex;
        margin-bottom: 10px;
        justify-content: flex-end;

        .hidden-padding-checkbox {
          padding-left: 14px;
          line-height: 30px;

          .label {
            padding-right: 10px;
            cursor: pointer;

            &.is-checked {
              color: #0096cc;
            }
          }
        }

        .title {
          line-height: 32px;
          margin-right: 10px;
        }

        .content-error {
          font-size: 12px;
          line-height: 32px;
          color: #F56C6C;
        }

        .edit-btn {
          margin-right: 0;
          padding: 0 5px;
          line-height: normal;

          i {
            font-size: 26px;
          }

          &.delete-btn {
            margin-left: auto;
            cursor: pointer;
          }

          &.drag-btn {
            cursor: grab;
          }
        }

        .btn-divider {
          height: auto;
          background-color: #aaaaaa;
        }
      }

      .content-item-list {
        display: flex;
        margin: 15px 0;
        border: 1px solid #DCDFE6;
        flex-flow: row wrap;

        .content-item {
          &.carousel-item,
          &.picture-item {
            display: flex;
            width: 170px;
            margin: 0 9px;
            padding: 10px;
            flex-direction: column;
            justify-content: space-between;
            align-items: center;

            .carousel-image-uploader,
            .picture-image-uploader {
              width: 100%;
              height: 150px;
            }

            .carousel-image-desc {
              margin: 10px 0;

              ::v-deep .el-input__inner {
                border-top: 0;
                border-left: 0;
                border-right: 0;
              }
            }
          }

          &.text-item {
            width: 100%;
          }

          &.video-item {
            margin: 0 auto;
            padding: 10px;
            text-align: center;

            .video-cover-uploader, .video-uploader {
              display: inline-block;
              vertical-align: middle;
              margin: 10px 20px 20px;
              width: 250px;
              height: 135px;
            }

            .video-desc {
              margin: 10px 0;
              width: 540px;

              ::v-deep .el-input__inner {
                border-top: 0;
                border-left: 0;
                border-right: 0;
              }
            }
          }

          &.job-description-item {
            width: 100%;
            padding: 20px;
            height: 100%;
            line-height: 30px;
            border: 1px solid rgb(242, 205, 0);
            background-color: rgb(251, 243, 141);
            text-align: center;
            font-size: 20px;
          }
        }
      }
    }
  }

  .add-btn-container {
    display: flex;
    padding: 15px;
    border: 1px solid #DCDFE6;
    border-radius: 5px;
    justify-content: space-around;

    .add-btn {
      display: flex;
      padding: 0px 20px;
      flex-direction: column;
      align-items: center;
      cursor: pointer;

      .btn-icon {
        width: 80px;
        height: 80px;
        background-repeat: no-repeat;
        background-size: cover;
      }

      .btn-label {
        padding-bottom: 0;

        ::v-deep span {
          display: inline-block;
          width: 100%;
          white-space: pre-wrap;
        }
      }

      &.add-carousel-btn {
        .btn-icon {
          background-image: url($baseDomain + 'static/image/icon_add_pic.png');
        }
      }
      &.add-text-btn {
        .btn-icon {
          background-image: url($baseDomain + 'static/image/icon_add_txt.png');
        }
      }

      &.add-picture-btn {
        .btn-icon {
          background-image: url($baseDomain + 'static/image/icon_add_grouppic.png');
        }
      }

      &.add-video-btn {
        .btn-icon {
          background-image: url($baseDomain + 'static/image/icon_add_video.png');
        }
      }
    }
  }
}
.has-custom-content {
  margin-top: 0;
}
</style>
